/*
 * App-wide intake template events from any chat. Casey's template tools
 * (`draft_intake_template` / `revise_intake_template` / `edit_intake` with a
 * `template_id`) report `invalidate: ["intake_templates"]`, and undoing one of
 * their changes does the same, so the Intake Templates list refetches
 * and an open editor picks up the new questions; their `open` hint
 * (`{kind: "template", id}`) opens the template in Firm Knowledge when that
 * page is showing.
 */
import { useEffect, useRef } from "react";

export const INTAKE_TEMPLATES_TARGET = "intake_templates";

type Listener = () => void;
type OpenListener = (templateId: string) => void;

const invalidated = new Set<Listener>();
const opened = new Set<OpenListener>();

/** Forward a `data-invalidate` list; only `intake_templates` matters here. */
export const notifyIntakeTemplatesInvalidated = (
  targets: readonly string[]
): void => {
  if (!targets.includes(INTAKE_TEMPLATES_TARGET)) return;
  for (const fn of Array.from(invalidated)) fn();
};

/** A `data-open-artifact` for a template. */
export const notifyIntakeTemplateOpened = (templateId: string): void => {
  for (const fn of Array.from(opened)) fn(templateId);
};

/** Run `onChange` when a chat changed the firm's templates (latest callback wins). */
export const useOnIntakeTemplatesInvalidated = (onChange: () => void): void => {
  const ref = useRef(onChange);
  ref.current = onChange;
  useEffect(() => {
    const listener = () => ref.current();
    invalidated.add(listener);
    return () => {
      invalidated.delete(listener);
    };
  }, []);
};

/** Run `onOpen` when a chat opened a template (latest callback wins). */
export const useOnIntakeTemplateOpened = (
  onOpen: (templateId: string) => void
): void => {
  const ref = useRef(onOpen);
  ref.current = onOpen;
  useEffect(() => {
    const listener = (id: string) => ref.current(id);
    opened.add(listener);
    return () => {
      opened.delete(listener);
    };
  }, []);
};
